import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { ApiError, type DiscordEstado } from '../api/types';
import { existe, t } from '../i18n/idioma';
import { Cartao } from './hud/Cartao';

/** O que a volta do Discord deixou na URL (`?discord=ok` ou `?discord=erro&motivo=…`), lido uma vez e apagado. */
export interface RetornoDiscord {
  ok: boolean;
  motivo: string;
}

/** Lê e tira `?discord=` da URL. Null quando a página não veio do Discord. */
export function lerRetornoDiscord(): RetornoDiscord | null {
  try {
    const q = new URLSearchParams(window.location.search);
    const v = q.get('discord');
    if (v !== 'ok' && v !== 'erro') return null;
    const motivo = (q.get('motivo') ?? '').replace(/[^a-zA-Z]/g, '').slice(0, 32);
    q.delete('discord');
    q.delete('motivo');
    const resto = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (resto ? `?${resto}` : '') + window.location.hash);
    return { ok: v === 'ok', motivo };
  } catch {
    return null;
  }
}

/** Texto do motivo de erro da volta. O motivo é um id fixo do servidor; desconhecido cai no genérico. */
function textoDoRetorno(r: RetornoDiscord): string {
  if (r.ok) return t('config.discord.vinculadoAgora');
  const chave = `config.discord.retorno.${r.motivo}`;
  return existe(chave) ? t(chave) : t('config.discord.retorno.recusado');
}

/**
 * Seção Discord das Configurações (techspec-discord-cargos §09): vincular, ver os cargos, atualizar e desvincular.
 * O jogo guarda só o id do Discord; a tela nunca mostra o id.
 */
export function DiscordCartao({ retorno }: { retorno: RetornoDiscord | null }) {
  const [estado, setEstado] = useState<DiscordEstado | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; erro: boolean } | null>(
    retorno ? { texto: textoDoRetorno(retorno), erro: !retorno.ok } : null);
  const [confirmar, setConfirmar] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setEstado(await api.discordEstado());
    } catch (e) {
      setAviso({ texto: e instanceof ApiError ? e.message : t('comum.semRede'), erro: true });
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const acao = async (f: () => Promise<void>) => {
    if (ocupado) return;
    setOcupado(true);
    setAviso(null);
    try {
      await f();
    } catch (e) {
      setAviso({ texto: e instanceof ApiError ? e.message : t('comum.semRede'), erro: true });
    } finally {
      setOcupado(false);
    }
  };

  const vincular = () => acao(async () => {
    const { url } = await api.discordVincular();
    // Só a URL do Discord (ou a volta do mock, na mesma origem).
    if (url.startsWith('https://discord.com/') || url.startsWith('/')) window.location.assign(url);
  });

  const atualizar = () => acao(async () => {
    setEstado(await api.discordAtualizar());
    setAviso({ texto: t('config.discord.atualizado'), erro: false });
  });

  const desvincular = () => acao(async () => {
    await api.discordDesvincular();
    setConfirmar(false);
    setAviso({ texto: t('config.discord.desvinculado'), erro: false });
    await carregar();
  });

  let corpo;
  if (!estado) {
    corpo = <div className="p-opt"><span><small>{t('comum.carregando')}</small></span></div>;
  } else if (!estado.ligado) {
    corpo = <div className="p-opt"><span><small>{t('config.discord.desligado')}</small></span></div>;
  } else if (!estado.vinculado) {
    corpo = (
      <div className="p-opt">
        <span><b>{t('config.discord.naoVinculado')}</b><small>{t('config.discord.porQue')}</small></span>
        <button type="button" className="btn" disabled={ocupado} onClick={() => { void vincular(); }}>
          {t('config.discord.vincular')}
        </button>
      </div>
    );
  } else {
    const fora = estado.erro === 'fora_do_servidor' || estado.erro === 'entrar_falhou';
    corpo = (
      <>
        <div className="p-opt">
          <span>
            <b>{t('config.discord.vinculado')}</b>
            <small>
              {estado.cargos.length > 0
                ? t('config.discord.cargos', { lista: estado.cargos.join(', ') })
                : estado.pendente ? t('config.discord.sincronizando') : t('config.discord.semCargos')}
            </small>
          </span>
          <button type="button" className="btn btn--ghost" disabled={ocupado} onClick={() => { void atualizar(); }}>
            {t('config.discord.atualizar')}
          </button>
        </div>
        {fora && (
          <p className="p-ajuda">
            {t('config.discord.foraDoServidor')}{' '}
            {estado.convite && <a href={estado.convite} target="_blank" rel="noopener noreferrer">{t('config.discord.entrarNoServidor')}</a>}
          </p>
        )}
        {estado.erro === 'discord_indisponivel' && <p className="p-ajuda">{t('config.discord.indisponivel')}</p>}
        <div className="p-opt">
          <span><small>{t('config.discord.desvincularAjuda')}</small></span>
          {confirmar ? (
            <span>
              <button type="button" className="btn btn--tiny" disabled={ocupado} onClick={() => { void desvincular(); }}>
                {t('config.discord.desvincularSim')}
              </button>{' '}
              <button type="button" className="btn btn--tiny btn--ghost" disabled={ocupado} onClick={() => setConfirmar(false)}>
                {t('comum.cancelar')}
              </button>
            </span>
          ) : (
            <button type="button" className="btn btn--tiny btn--ghost" disabled={ocupado} onClick={() => setConfirmar(true)}>
              {t('config.discord.desvincular')}
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <Cartao titulo={t('config.discord.titulo')} desc={t('config.discord.desc')}>
      {corpo}
      {aviso && <p className={aviso.erro ? 'p-alerta' : 'p-ajuda'} role="status">{aviso.texto}</p>}
    </Cartao>
  );
}
